import Avo from 'analytics/Avo';
import type { PricingList } from 'api/types/pricing.types';
import { enableMapSet, produce } from 'immer';
import { create } from 'zustand';

import { createSelectors } from '../utils/create-selectors';
import { usePricingStore } from './pricing.store';

enableMapSet();

const categoryNames = ['HOME_CARE', 'HomeCare', 'Bed & Bath', 'WASH_FOLD'];

export interface CartItem extends PricingList.ItemType {
  quantity: number;
  category: string;
}

interface CartState {
  cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>;
  add: (item: CartItem, serviceType: PricingList.ServiceTypes) => void;
  remove: (item: CartItem, serviceType: PricingList.ServiceTypes) => void;
  clear: () => void;
  clearCategory: (category: string, serviceType: PricingList.ServiceTypes) => void;
}

const updateQuantity = (
  cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>,
  item: PricingList.ItemType,
  serviceType: PricingList.ServiceTypes,
  delta: number
) => {
  const cartItems = cart.get(serviceType) || new Map();
  const itemKey = item.key || 'bag';
  const quantity = cartItems.get(itemKey)?.quantity || 0;

  if (quantity + delta > 0) {
    return produce(cart, (draft) => {
      draft.set(
        serviceType,
        produce(cartItems, (itemDraft) => {
          itemDraft.set(itemKey, { ...item, quantity: quantity + delta });
        })
      );
    });
  }
  return produce(cart, (draft) => {
    draft.get(serviceType)?.delete(itemKey);
    if (draft.get(serviceType)?.size === 0) {
      draft.delete(serviceType);
    }
  });
};

const useCartEstimatorBase = create<CartState>((set, get) => ({
  cart: new Map<PricingList.ServiceTypes, Map<string, CartItem>>(),
  add: (item: CartItem, serviceType: PricingList.ServiceTypes) => {
    const { cart } = get();

    set({
      cart: updateQuantity(cart, item, serviceType, 1),
    });

    if (categoryNames.includes(item.category)) {
      Avo.estimatorBagAdded({ bagType: item.key });
    } else {
      Avo.estimatorItemAdded({ itemName: item.name });
    }
  },
  remove: (item: CartItem, serviceType: PricingList.ServiceTypes) => {
    const { cart } = get();

    set({
      cart: updateQuantity(cart, item, serviceType, -1),
    });
    if (categoryNames.includes(item.category)) {
      Avo.estimatorBagRemoved({ bagType: item.key });
    } else {
      Avo.estimatorItemRemoved({ itemName: item.name });
    }
  },
  clear: () => set(() => ({ cart: new Map<PricingList.ServiceTypes, Map<string, CartItem>>() })),
  clearCategory: (category: string, serviceType: PricingList.ServiceTypes) => {
    const { cart } = get();
    cart.get(serviceType)?.forEach((item: CartItem) => {
      if (item.category === category) {
        set({
          cart: updateQuantity(cart, item, serviceType, -item.quantity),
        });
      }
    });
  },
}));

const useCartEstimator = createSelectors(useCartEstimatorBase);

export const useItemQuantity = (serviceType: PricingList.ServiceTypes, itemKey: string) =>
  useCartEstimator((state: CartState) => state.cart.get(serviceType)?.get(itemKey)?.quantity || 0);

const getTotalLaundryBill = (cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>) => {
  let bill = 0;

  cart.forEach((itemList) => {
    itemList.forEach((item) => {
      bill += item.quantity * (item.discountedPrice ? item.discountedPrice : item.price);
    });
  });
  return bill;
};

const getMinOrderTopUp = (cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>, billingConstants: PricingList.BillingConstants) => {
  const { minimumBeforeVat, discountedMinimumBeforeVat } = billingConstants.charges;
  const minCharge = discountedMinimumBeforeVat || minimumBeforeVat;

  const { delivery, discountedDelivery } = billingConstants.charges;
  const deliveryFee = discountedDelivery ?? delivery;

  const fee = minCharge - deliveryFee - getTotalLaundryBill(cart);
  return fee > 0 ? fee : 0;
};

const getTotalEstimatedBillBeforeTax = (
  cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>,
  billingConstants: PricingList.BillingConstants
) => {
  const { delivery, discountedDelivery } = billingConstants.charges;
  return getMinOrderTopUp(cart, billingConstants) + getTotalLaundryBill(cart) + (discountedDelivery ?? delivery);
};

const getTotalEstimatedBill = (cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>, billingConstants: PricingList.BillingConstants) => {
  const vat = billingConstants.charges.vatPercentage;

  let bill = getTotalEstimatedBillBeforeTax(cart, billingConstants);

  bill += bill * vat;

  return bill;
};

export const useShowEstimator = () => useCartEstimatorBase((state: CartState) => state.cart.size > 0);

export const useCartItemsCount = () =>
  useCartEstimatorBase((state: CartState) => {
    let count = 0;
    state.cart.forEach((itemList) => {
      itemList.forEach((item) => {
        count += item.quantity;
      });
    });
    return count;
  });

export const useBillEstimator = () => {
  const billingConstants = usePricingStore.use.billingConstants();
  return useCartEstimatorBase((state: CartState) => ({
    totalLaundryBill: getTotalLaundryBill(state.cart),
    totalEstimatedBillBeforeTax: getTotalEstimatedBillBeforeTax(state.cart, billingConstants),
    totalEstimatedBill: getTotalEstimatedBill(state.cart, billingConstants),
    minOrderTopUp: getMinOrderTopUp(state.cart, billingConstants),
  }));
};

const shouldDisableLinens = (cart: Map<PricingList.ServiceTypes, Map<string, CartItem>>) => {
  const hcService = cart.get('HOME_CARE');
  return hcService ? hcService.size > 0 : false;
};

export const useDisableLinens = () => useCartEstimatorBase((state: CartState) => shouldDisableLinens(state.cart));

export default useCartEstimator;
